import type { VehicleType } from "@daytrip/legacy-enums";
import { DriverStatus } from "@daytrip/legacy-enums";
import { Exclude, Expose, Type } from "class-transformer";
import {
    IsArray,
    IsBoolean,
    IsDate,
    IsDefined,
    IsInt,
    IsString,
    IsUUID,
    Validate,
    ValidateIf,
    IsNumber,
    IsNotEmpty,
} from "class-validator";

import { statusGetter } from "../statusGetter";
import { TransformToDate } from "../transformers/transformToDate";
import { IsIbanValidator } from "../validators/IsIbanValidator";

import { AcceptanceRateInfo } from "./AcceptanceRateInfo";
import { CooperationAgreement } from "./CooperationAgreement";
import { Document } from "./Document";
import { KnownLanguage } from "./KnownLanguage";
import { Position } from "./Position";
import { Score } from "./Score";

@Exclude()
export class Driver {
    @IsString()
    @IsDefined()
    @Expose()
    public managementNote: string = "";

    @IsInt()
    @IsDefined()
    @Expose()
    public willingToSleepoverCount: number = 1; // how many times is willing to sleep over

    @IsDate()
    @Expose()
    @TransformToDate
    public drivingLicenseGrantedAt?: Date;

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => KnownLanguage)
    public languages: KnownLanguage[] = [];

    @IsString()
    @IsDefined()
    @Expose()
    public town: string = "";

    @IsString()
    @IsNotEmpty({ always: true, message: "Hometown could not be empty" })
    @Expose()
    public address: string = "";

    @Expose()
    @Type(() => Position)
    public position?: Position;

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public originLocationIds: string[] = [];

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Number)
    public willingToRentVehicleTypes: VehicleType[] = [];

    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => Document)
    public documents: Document[] = [];

    @Validate(IsIbanValidator)
    @ValidateIf((driver: Driver) => driver.driversCompanyUserId == null)
    @IsString()
    @Expose()
    public iban?: string;

    @IsUUID("4")
    @Expose()
    public driversCompanyUserId?: string;

    @IsDate()
    @Expose()
    @TransformToDate
    public hiredAt?: Date = new Date();

    @IsString()
    @Expose()
    public hireReason?: string;

    /**
     * This is the date when the driver was approved by support and changed to Active state.
     */
    @IsDate()
    @Expose()
    @TransformToDate
    public activeAt?: Date;

    @IsDate()
    @Expose()
    @TransformToDate
    public declinedAt?: Date;

    @IsDate()
    @Expose()
    @TransformToDate
    public premiumFrom?: Date;

    @Expose()
    @Type(() => Score)
    public score: Score = new Score();

    @Expose()
    @Type(() => AcceptanceRateInfo)
    public acceptanceRateInfo?: AcceptanceRateInfo = new AcceptanceRateInfo();

    @Expose()
    @Type(() => CooperationAgreement)
    public cooperationAgreement?: CooperationAgreement;

    @Expose()
    @IsBoolean()
    public hasCooperationAgreement?: boolean;

    public get status(): DriverStatus {
        return statusGetter(this, Driver, DriverStatus);
    }

    public static statusPrototype(status: DriverStatus): Driver {
        const defined = new Date();

        switch (status) {
            case DriverStatus.Declined:
                return {
                    declinedAt: defined,
                } as Driver;

            case DriverStatus.Active:
                return {
                    activeAt: defined,
                    declinedAt: undefined,
                } as Driver;

            default:
            case DriverStatus.Hired:
                return {
                    hiredAt: defined,
                    declinedAt: undefined,
                    activeAt: undefined,
                } as Driver;
        }
    }

    @IsUUID("4")
    @Expose()
    public hiredByUserId?: string;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public isSuitableForLuxury: boolean = false;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public isSuitableForPool: boolean = false;

    @IsBoolean()
    @Expose()
    public declinedByCompany: boolean = false;

    @IsNumber()
    @Expose()
    public acquisitionCost?: number;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public canSpeakEnglish: boolean = false;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public willingToDoLite: boolean = true;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public isTCPDriver: boolean = false;

    /**
     * Only applicable to US drivers.
     * Set to null or not set at all for non-US drivers.
     */
    @IsUUID("4")
    @Expose()
    public regionId?: string;

    @IsBoolean()
    @IsDefined()
    @Expose()
    public isNotDomesticVATPayer: boolean = false;
}
